import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { UserProfilePreviewRelay } from '@atlassian/user-profile-preview/relay';

import type { peopleEntityProfileRelayQuery } from './__generated__/peopleEntityProfileRelayQuery.graphql';

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

export type PeopleEntityProfileRelayProps = {
	accountId: string;
	cloudId: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
};

/**
 * Loads profile, recent work, smarts, and reporting lines via one Relay query (same shape as
 * search-ai `individualPersonKnowledgeCardRelayQuery`)..
 */
export function PeopleEntityProfileRelay({
	accountId,
	cloudId,
	orgId,
	requestingUserId,
	sourceProduct,
}: PeopleEntityProfileRelayProps): React.JSX.Element {
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;

	const data = useLazyLoadQuery<peopleEntityProfileRelayQuery>(
		graphql`
			query peopleEntityProfileRelayQuery(
				$first: Int!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
				$singleUserAccountId: String!
				$singleUserRequestingUserId: String!
				$singleUserTenantId: String!
			) {
				activity {
					workedOn(first: $first, filter: $filter) {
						...UserProfilePreviewRelay_workedOn
					}
				}
				smarts {
					...UserProfilePreviewRelay_smarts @arguments(recommendationsQuery: $recommendationsQuery)
				}
				knowledgeDiscovery {
					...UserProfilePreviewRelay_user
						@arguments(
							accountId: $singleUserAccountId
							requestingUserId: $singleUserRequestingUserId
							tenantId: $singleUserTenantId
						)
					...UserProfilePreviewRelay_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
				}
			}
		`,
		{
			first: 50,
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
			filter: { actors: [accountIdARI] },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller: 'lumina',
					experience: 'CgUserNearbyUser',
				},
				requestingUserId,
				maxNumberOfResults: 8,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);

	return (
		<UserProfilePreviewRelay
			userFragment={data.knowledgeDiscovery ?? undefined}
			workedOnFragment={data.activity?.workedOn ?? undefined}
			smartsFragment={data.smarts ?? undefined}
			reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
			cloudId={cloudId}
			testId="lumina-people-entity-profile"
		/>
	);
}
